import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Spin } from "antd";
import UserFeedbacksTable from "../components/UserFeedbacksTable";
import { UTILS } from "utils";
import { API } from "api";
import { errorHandler } from "api/api/errorHandler";
import useAllStores from "hooks/useCurrentUserStores";
import { successNotification } from "utils/Utils";
import AppButton from "components/ui/AppButton";
import AppSelect from "components/ui/AppSelect";
import AppSwitch from "components/ui/AppSwitch";

const OrderTypes = ["All", "DELIVERY", "PICKUP", "DINE IN"];
const RatingTypes = ["All", "Bad", "Excellent"];
const ALL = "All";

const UserFeedbacks = () => {
  const { t } = useTranslation();
  const { stores, loading, setLoading } = useAllStores();
  const [state, setState] = useState({
    error: null,
    userFeedbacks: [],
    allUserFeedbacks: [],
    selectedStore: ALL,
    selectedOrderType: null,
    selectedRatingType: null,
    isCommentsOnly: false,
    currentPage: 1,
    showLoadMore: true,
  });

  const updateState = (key, value) =>
    setState((prevState) => ({ ...prevState, [key]: value }));

  const buildApiRequestParams = () => {
    const params = {
      brandId: UTILS.getCurrentUser()?.brandId,
      page: state.currentPage,
      limit: 10,
    };

    if (state?.selectedStore && state?.selectedStore !== ALL) {
      params.storeId = state.selectedStore;
    }

    if (state?.selectedOrderType && state?.selectedOrderType !== ALL) {
      params.serviceType = state.selectedOrderType;
    }

    if (state.isCommentsOnly) {
      params.isCommentsOnly = state.isCommentsOnly;
    }

    if (state?.selectedRatingType && state?.selectedRatingType !== ALL) {
      params.rating = state.selectedRatingType;
    }

    return params;
  };

  const fetchAllOrdersFeedbacks = async () => {
    try {
      setLoading(true);
      const result = await API.getOrdersBacks(buildApiRequestParams());

      if (result?.success === 1 && result?.data?.feedbacks) {
        const newData = result.data?.feedbacks;
        const allfeedacks = [...state.allUserFeedbacks, ...newData];
        updateState("allUserFeedbacks", allfeedacks);
        updateState("userFeedbacks", result?.data);
        updateState("currentPage", state.currentPage + 1);

        if (newData.length <= 0) {
          updateState("showLoadMore", false);
          successNotification(
            "You've reached the end of the feedbacks. No more data to load"
          );
        }
      } else {
        UTILS.handleErrorNotification(
          errorHandler(result?.error?.message || "Error fetching data")
        );
      }
    } catch (error) {
      UTILS.handleErrorNotification(
        errorHandler(error?.message || "An unexpected error occurred")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const userDetail = UTILS.getCurrentUser();
    if (!userDetail?.brandId) return;
    fetchAllOrdersFeedbacks();
  }, [
    state.selectedStore,
    state.selectedOrderType,
    state.isCommentsOnly,
    state.selectedRatingType,
  ]);

  const handleChangeOptions = (key, val) => {
    updateState("showLoadMore", true);
    updateState("allUserFeedbacks", []);
    updateState("userFeedbacks", []);
    updateState("currentPage", 1);
    updateState(key, val);
  };

  const storeSelectOptions = useMemo(
    () => [
      { value: ALL, label: t("All Stores") },
      ...(stores?.map((store) => ({
        value: store.value,
        label: store.label,
      })) || []),
    ],
    [stores, t]
  );

  const orderTypeOptions = useMemo(
    () => OrderTypes.map((opt) => ({ value: opt, label: t(opt) })),
    [t]
  );

  const ratingOptions = useMemo(
    () => RatingTypes.map((opt) => ({ value: opt, label: t(opt) })),
    [t]
  );

  return (
    <div className="w-full rounded-3xl bg-white p-5 md:p-8">
      <Spin spinning={loading}>
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 md:text-3xl">
            {t("Order Feedbacks")}
          </h1>
        </div>

        <section className="mb-8 rounded-2xl border border-gray-100 bg-gray-50/90 p-4 md:p-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            <AppSelect
              placeholder={t("Select Store")}
              style={{ width: "100%" }}
              value={
                state.selectedStore === null || state.selectedStore === ""
                  ? undefined
                  : state.selectedStore
              }
              onChange={(value) => handleChangeOptions("selectedStore", value)}
              options={storeSelectOptions}
            />
            <AppSelect
              placeholder={t("All Order Types")}
              style={{ width: "100%" }}
              value={
                state.selectedOrderType === null ||
                state.selectedOrderType === ""
                  ? undefined
                  : state.selectedOrderType
              }
              onChange={(value) =>
                handleChangeOptions("selectedOrderType", value)
              }
              options={orderTypeOptions}
            />
            <AppSelect
              placeholder={t("All Ratings")}
              style={{ width: "100%" }}
              value={
                state.selectedRatingType === null ||
                state.selectedRatingType === ""
                  ? undefined
                  : state.selectedRatingType
              }
              onChange={(value) =>
                handleChangeOptions("selectedRatingType", value)
              }
              options={ratingOptions}
            />
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-gray-200/80 pt-4">
            <AppSwitch
              checked={state.isCommentsOnly}
              label={t("Only With Notes")}
              onChange={(checked) =>
                handleChangeOptions("isCommentsOnly", checked)
              }
            />
          </div>
        </section>

        <UserFeedbacksTable
          feedbacks={state.allUserFeedbacks}
          avgOrderRating={state.userFeedbacks?.avgOrderRating}
          feedbackRatingCount={state.userFeedbacks?.feedbackRatingCount}
          totalCount={state.userFeedbacks?.totalCount}
        />
        {state?.showLoadMore && (
          <div className="mt-6 flex justify-center">
            <AppButton
              variant="primary"
              loading={loading}
              disabled={loading}
              onClick={() => fetchAllOrdersFeedbacks()}
            >
              {t("Load More")}
            </AppButton>
          </div>
        )}
      </Spin>
    </div>
  );
};

export default UserFeedbacks;
